const DefaultRenderer = require("./defaultrenderer");
const rbush = require("rbush");

/**
 * @extends woosmap.layers.renderers.DefaultRenderer
 * @memberOf woosmap.layers.renderers
 * @since 1.2.0
 */
class MarkersRenderer extends DefaultRenderer {
    /**
     * Markers renderer.
     * @param {RendererOptions} options
     * @constructor
     */
    constructor(options) {
        super(options);

        /**
         * @type {google.maps.Map|BMap.Map}
         * @private
         */
        this._map = undefined;

        /**
         * Spatial index
         * @type rbush
         * @private
         */
        this._tree = rbush(9);

        /**
         * Feature Ids cache
         * @private
         */
        this._loadedFeatureIds = {};
    }

    _registerStoreEventTriggers() {}

    /**
     * @param {GeoJSON} stores
     * @private
     */
    _setIdleListener(stores) {
        this._idleListener = this.getMap().addListener("idle", () => {
            this.displayMarkersForStores(stores);
        });
    }

    /**
     * @private
     */
    _clearIdleListener() {
        if (this._idleListener !== undefined) {
            google.maps.event.removeListener(this._idleListener);
        }
    }

    /**
     * Set the map instance for each markers
     * @param map
     * @private
     */
    _setMapForEachMarker(map) {
        this._tree.all().forEach((el) => {
            el[4].setMap(map);
        });
    }

    /**
     * Build the rtree coordinates for a specific point
     * @param {number} sw_lng
     * @param {number} sw_lat
     * @param {number} ne_lng
     * @param {number} ne_lat
     * @returns {Array.<Number>}
     * @private
     */
    _coordinatesRtree(sw_lng, sw_lat, ne_lng, ne_lat) {
        return [sw_lng, sw_lat, ne_lng, ne_lat];
    }

    /**
     * Build the rtree coordinates for a feature
     * @param feature
     * @returns {Array.<Number>}
     * @private
     */
    _coordinatesRtreeForFeature(feature) {
        const coordinates = feature.geometry.coordinates;
        return this._coordinatesRtree(coordinates[0], coordinates[1], coordinates[0], coordinates[1]);
    }

    /**
     * Serialize a feature to load it in a rtree
     * @param feature
     * @param isSelected
     * @param number
     * @returns {Array.<Number>}
     * @private
     */
    _serializeFeatureForRtree(feature, isSelected, number) {
        const serialized = this._coordinatesRtreeForFeature(feature);
        serialized.push(
            this._markerForFeature(feature, {
                selected: isSelected,
                number: number,
            }),
        );
        serialized.push(feature);
        return serialized;
    }

    /**
     * Get the marker style for a feature
     * @param feature
     * @param options
     * @returns {google.maps.Icon}
     * @private
     */
    _getIconForFeature(feature, options) {
        return this.styler.getMarkerStyleForStore(feature, options);
    }

    /**
     * Build the google.maps.Marker's options for a feature
     * @param feature
     * @param options
     * @returns {{position: {lat: number, lng: number}, icon: google.maps.Icon}}
     * @private
     */
    _markerOptionsForFeature(feature, options) {
        return {
            position: {
                lat: feature.geometry.coordinates[1],
                lng: feature.geometry.coordinates[0],
            },
            icon: this._getIconForFeature(feature, options),
        };
    }

    /**
     * This is the listener registered for each supported events on markers.
     * @param eventName
     * @param store
     * @param marker
     * @private
     */
    _universalListener(eventName, store, marker) {
        this._storeEventManager.triggerWithSource(this, eventName, store, marker);
    }

    /**
     * Registers events for
     * @param {google.maps.Marker} marker
     * @param {Store} feature
     * @private
     */
    _buildMarkerListener(marker, feature) {
        const self = this;

        function registerEvent(eventName, feature, marker) {
            google.maps.event.clearListeners(marker, eventName);
            marker.addListener(eventName, () => {
                self._universalListener(eventName, feature, marker);
            });
        }

        if (this._storeEventManager) {
            const supportedEvents = this._storeEventManager.supportedEvents();

            for (const supportedEventKey in supportedEvents) {
                if (supportedEvents.hasOwnProperty(supportedEventKey)) {
                    const supportedEvent = supportedEvents[supportedEventKey];
                    registerEvent(supportedEvent, feature, marker);
                }
            }
        }
    }

    /**
     * Build a Marker instance for a specific feature
     * @param feature
     * @param options
     * @returns {google.maps.Marker}
     * @private
     */
    _markerForFeature(feature, options) {
        const marker = new google.maps.Marker(this._markerOptionsForFeature(feature, options));

        this._buildMarkerListener(marker, feature);

        return marker;
    }

    /**
     * Return the markers and the features objects for a subtree of _tree
     * @param tree
     * @returns {{markers: Array<google.maps.Marker>, features: Array}}
     * @private
     */
    _getMarkersAndFeatureForTree(tree) {
        const markers = [];
        const features = [];
        tree.forEach((data) => {
            markers.push(data[4]);
            features.push(data[5]);
        });
        return { markers: markers, features: features };
    }

    /**
     * Return the markers and the features objects for the current bounds
     * @returns {{markers, features}|{markers: Array.<google.maps.Marker>, features: Array}}
     * @private
     */
    _getDisplayingObject() {
        return this._getMarkersAndFeatureForTree(this._subtreeForCurrentBounds());
    }

    /**
     * Load the geojson in the rtree property
     * @param {GeoJSON} geojson
     * @param {boolean} [isNumbered]
     * @private
     */
    _loadGeojsonInRtree(geojson, isNumbered) {
        const featuresToLoad = [];
        let isSelected;
        let number;
        const features = geojson.features;

        features.forEach((feature) => {
            const storeId = feature.properties.store_id;
            number = isNumbered ? this._getNumberToFeatureForNumberedIcon(feature, features) : undefined;
            isSelected = this._setLastIconIfFeatureIsSelected(feature, number);

            if (this._loadedFeatureIds[storeId] === undefined) {
                featuresToLoad.push(this._serializeFeatureForRtree(feature, isSelected, number));
                this._loadedFeatureIds[storeId] = true;
            }
        });

        this._tree.load(featuresToLoad);
    }

    /**
     * Return the feature number for the numbered icons
     * @param {Store} feature
     * @param {Array.<Store>} features
     * @returns {number}
     * @private
     */
    _getNumberToFeatureForNumberedIcon(feature, features) {
        return this._dataStyler._findStoreIndex(feature.properties.store_id, features) + 1;
    }

    /**
     * Load the stores geojson, set the listener and display the markers
     * @param {Boolean} isNumbered
     * @param {GeoJSON} stores
     * @private
     */
    _displayStores(isNumbered, stores) {
        this._loadGeojsonInRtree(stores, isNumbered);
        this._setIdleListener(stores);
        this.displayMarkersForStores(stores);
    }

    /**
     * Return the current bounds subtree
     * @returns {*}
     * @private
     */
    _subtreeForCurrentBounds() {
        if (this.getMap()) {
            const mapBounds = this.getMap().getBounds();
            const mB_NE = mapBounds.getNorthEast();
            const mB_SW = mapBounds.getSouthWest();
            return this._tree.search(this._coordinatesRtree(mB_SW.lng(), mB_SW.lat(), mB_NE.lng(), mB_NE.lat()));
        }
        return [];
    }

    /**
     * Check if a feature is the selectedStore
     * @param feature
     * @returns {boolean}
     * @private
     */
    _featureIsSelectedStore(feature) {
        if (!this._selectedStore) {
            return false;
        }
        return this._selectedStore.properties.store_id === feature.properties.store_id;
    }

    /**
     * Set the _lastIcon property if the feature is selected, and return the isSelected value
     * @param feature
     * @param {number} number
     * @returns {boolean}
     * @private
     */
    _setLastIconIfFeatureIsSelected(feature, number) {
        const isSelected = this._featureIsSelectedStore(feature);
        if (isSelected) {
            this._lastIcon = this._getIconForFeature(feature, { selected: false, number: number });
        }
        return isSelected;
    }

    /**
     * Return the marker linked to a store
     * @param store
     * @returns {google.maps.Marker}
     * @private
     */
    _getMarkerForStore(store) {
        const marker = this._tree.search(this._coordinatesRtreeForFeature(store))[0];
        if (marker === undefined) {
            return undefined;
        }
        return marker[4];
    }

    /**
     * Set the icon of the store's marker
     * @param {Store} store
     * @param {boolean} isSelected
     * @private
     */
    _setSelectedMarkerForStore(store, isSelected) {
        const marker = this._getMarkerForStore(store);
        let icon;
        if (marker !== undefined) {
            if (isSelected) {
                this._lastIcon = marker.getIcon();
                icon = this.styler.getMarkerStyleForStore(store, { selected: isSelected });
            }
            marker.setIcon(icon || this._lastIcon);
        }
    }

    /**
     * Display the markers of each features
     * @param {Array.<Store>} features
     * @param {google.maps.Marker} [markers]
     */
    displayMarkers(features, markers) {
        features.forEach((feature, index) => {
            const marker = markers !== undefined ? markers[index] : this._getMarkerForStore(feature);
            marker.setMap(this.getMap());
        });
    }

    /**
     * Hide the markers and remove the idleListener
     * @override
     */
    clear() {
        this._setMapForEachMarker(null);
        this._clearIdleListener();
        this._tree.clear();
        this._loadedFeatureIds = {};
    }

    /**
     * Set the map instance.
     * @override
     * @param {undefined|google.maps.Map} map
     */
    setMap(map) {
        this._map = map;
        this._setMapForEachMarker(map);
    }

    /**
     * Return the map instance
     * @returns {undefined|google.maps.Map}
     */
    getMap() {
        return this._map;
    }

    /**
     * Return the markers object for the current bounds
     * @returns {Array.<google.maps.Marker>}
     */
    getDisplayingMarkers() {
        return this._getDisplayingObject().markers;
    }

    /**
     * Return the features objects for the current bounds
     * @returns {Array}
     */
    getDisplayingFeatures() {
        return this._getDisplayingObject().features;
    }

    /**
     * @override
     * @param {GeoJSON} geojson
     */
    tileDidLoad(geojson) {
        this._loadGeojsonInRtree(geojson);
    }

    /**
     * Display the markers in the current bounds
     */
    displayCurrentMarkers() {
        const _object = this._getDisplayingObject();
        const features = _object.features;
        const markers = _object.markers;
        this.displayMarkers(features, markers);
    }

    /**
     * Display the marker for a stores list
     * @param {GeoJSON} stores
     */
    displayMarkersForStores(stores) {
        this.displayMarkers(stores.features);
    }

    /**
     * Display the stores list
     * @param {GeoJSON} stores
     */
    displayStores(stores) {
        this._displayStores(false, stores);
    }

    /**
     * Display the stores list with a numbered style
     * @param {GeoJSON} stores
     */
    displayNumberedStores(stores) {
        this._displayStores(true, stores);
    }

    /**
     * @override
     */
    tilesDidLoad() {
        this.displayCurrentMarkers();
    }

    /**
     * @override
     * @param {Store} data
     */
    selectStore(data) {
        if (this._selectedStore) {
            this._setSelectedMarkerForStore(this._selectedStore, false);
        }
        if (data !== null) {
            this._setSelectedMarkerForStore(data, true);
        }
        super.selectStore(data);
    }
}

module.exports = MarkersRenderer;
